import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';

type PromptOption = { title: string; description: string; prompt: string };

type RouterState = {
  prompts: PromptOption[];
  imageAnalysis: string;
  productName: string;
  aspectRatio: string;
  style: string;
  originalImageUrl: string;
  originalImageStoragePath: string;
} | null;

export function PromptSelectionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { userData, refreshUserData } = useAuth();
  const routerState = location.state as RouterState;

  const [prompts, setPrompts] = useState<PromptOption[]>(routerState?.prompts ?? []);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');
  const [blockedFeedback, setBlockedFeedback] = useState(false);

  useEffect(() => {
    if (!id) return;
    // Verify generation is still in draft (handles refresh / back-button)
    api.get<{ status: string }>(`/api/generations/${id}`)
      .then((gen) => {
        if (gen.status !== 'draft') {
          navigate(`/generation/${id}`, { replace: true });
        }
      })
      .catch(() => {
        // If fetch fails, stay on page — prompts from router state are still usable
      });
  }, [id]);

  // If no router state (user refreshed or accessed URL directly), redirect to dashboard
  if (prompts.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col items-center justify-center min-h-[40vh] gap-4">
          <p className="text-muted-foreground text-sm">
            {t('promptSelection.notFound', 'Página não encontrada. Redirecionando...')}
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            {t('promptSelection.backToDashboard', 'Voltar ao início')}
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const freeRemaining = Math.max(0, 2 - (userData?.freeGenerationsUsed ?? 0));
  const credits = userData?.credits ?? 0;

  const togglePrompt = (index: number) => {
    setSelectedIndices((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      if (prev.length >= 3) {
        // Flash "máximo 3 prompts" feedback
        setBlockedFeedback(true);
        setTimeout(() => setBlockedFeedback(false), 2000);
        return prev;
      }
      return [...prev, index];
    });
  };

  const handleRegenerate = async () => {
    if (!routerState || regenerating) return;
    setRegenerating(true);
    setError('');
    try {
      const result = await api.post<{ id: string; imageAnalysis: string; prompts: PromptOption[] }>(
        '/api/generations/prompts',
        {
          productName: routerState.productName,
          originalImageUrl: routerState.originalImageUrl,
          originalImageStoragePath: routerState.originalImageStoragePath,
          style: routerState.style,
          aspectRatio: routerState.aspectRatio,
        }
      );
      setPrompts(result.prompts);
      setSelectedIndices([]);
      navigate(`/generation/${result.id}/prompts`, {
        replace: true,
        state: { ...routerState, prompts: result.prompts },
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || 'Falha ao regerar sugestões');
    } finally {
      setRegenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedIndices.length === 0 || !id) return;
    setLoading(true);
    setError('');
    try {
      await api.post(`/api/generations/${id}/generate`, {
        selectedPromptIndices: selectedIndices,
      });
      refreshUserData();
      navigate(`/generation/${id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || t('promptSelection.error', 'Falha ao iniciar geração'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold">{t('promptSelection.title', 'Escolha suas cenas')}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('promptSelection.subtitle', 'Selecione até 3 prompts que mais combinam com sua visão')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            disabled={regenerating || loading}
            className="flex-shrink-0 gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? 'animate-spin' : ''}`} />
            {regenerating ? 'Regerando...' : 'Regerar'}
          </Button>
        </div>

        {/* Credit info badge */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted text-sm">
          {freeRemaining > 0 ? (
            <span>
              {t('promptSelection.freeRemaining', '{{count}} geração(ões) gratuita(s) restante(s)', { count: freeRemaining })}
            </span>
          ) : (
            <span>
              {t('promptSelection.credits', '{{count}} crédito(s) disponível(is)', { count: credits })}
            </span>
          )}
        </div>

        {/* Selection counter */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">
            {t('promptSelection.selected', '{{count}} de 3 selecionados', { count: selectedIndices.length })}
          </span>
          {blockedFeedback && (
            <span className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {t('promptSelection.maxReached', 'Máximo 3 prompts')}
            </span>
          )}
        </div>

        {/* 5 Prompt cards — vertical list, full width on mobile */}
        <div className="space-y-3">
          {regenerating
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="w-full p-4 rounded-xl border-2 border-border">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-muted animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded animate-pulse" style={{ width: `${65 + (i % 3) * 12}%` }} />
                      <div className="h-4 bg-muted rounded animate-pulse" style={{ width: `${40 + (i % 2) * 15}%` }} />
                    </div>
                  </div>
                </div>
              ))
            : prompts.map((prompt, index) => {
                const isSelected = selectedIndices.includes(index);
                const isDisabled = !isSelected && selectedIndices.length >= 3;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => togglePrompt(index)}
                    disabled={loading}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all
                      ${isSelected ? 'border-primary bg-primary/5' : ''}
                      ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-primary/50'}
                      ${!isSelected && !isDisabled ? 'border-border' : ''}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'}`}>
                        {isSelected && <CheckCircle2 className="w-3 h-3 text-primary-foreground" />}
                      </div>
                      <div>
                    <p className="text-base font-semibold leading-snug">{prompt.title}</p>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{prompt.description}</p>
                  </div>
                    </div>
                  </button>
                );
              })}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Spacer so content isn't hidden behind fixed bar on mobile */}
        <div className="h-20 md:hidden" />

        {/* Desktop: inline button */}
        <Button
          onClick={handleGenerate}
          disabled={selectedIndices.length !== 3 || loading}
          className="w-full hidden md:flex"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('promptSelection.generating', 'Iniciando geração...')}
            </>
          ) : (
            t('promptSelection.generate', 'Gerar imagens ({{count}})', { count: selectedIndices.length })
          )}
        </Button>
      </div>

      {/* Mobile: fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
        <Button
          onClick={handleGenerate}
          disabled={selectedIndices.length !== 3 || loading}
          className="w-full"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('promptSelection.generating', 'Iniciando geração...')}
            </>
          ) : (
            t('promptSelection.generate', 'Gerar imagens ({{count}})', { count: selectedIndices.length })
          )}
        </Button>
      </div>
    </DashboardLayout>
  );
}
